import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native';

import { colors, radii, spacing } from '@/constants/theme';

export interface PinInputHandle {
  focus(): void;
  clear(): void;
}

interface PinInputProps {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  onComplete?: (value: string) => void;
  hasError?: boolean;
  autoFocus?: boolean;
  editable?: boolean;
  /** 'light' = white cells on light bg. 'dark' = transparent cells on Deep Green. */
  variant?: 'light' | 'dark';
}

const DEFAULT_LENGTH = 6;

export const PinInput = forwardRef<PinInputHandle, PinInputProps>(function PinInput(
  {
    length = DEFAULT_LENGTH,
    value,
    onChange,
    onComplete,
    hasError,
    autoFocus,
    editable = true,
    variant = 'dark',
  },
  ref,
) {
  const inputRef = useRef<TextInput>(null);
  const [focused, setFocused] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    clear: () => onChange(''),
  }));

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, length);
    onChange(digits);
    if (digits.length === length) onComplete?.(digits);
  };

  const cells = Array.from({ length }, (_, i) => i);
  const variantStyles = variant === 'dark' ? darkStyles : lightStyles;

  return (
    <View style={styles.wrapper}>
      <View style={styles.row} pointerEvents="none">
        {cells.map((i) => {
          const isFilled = i < value.length;
          const isCursor = focused && i === value.length;
          return (
            <View
              key={i}
              style={[
                styles.cell,
                variantStyles.cell,
                isCursor && variantStyles.cellActive,
                hasError && variantStyles.cellError,
                !editable && styles.cellDisabled,
              ]}
            >
              {isFilled && (
                <View
                  style={[styles.dot, variantStyles.dot, hasError && variantStyles.dotError]}
                />
              )}
            </View>
          );
        })}
      </View>
      <TextInput
        ref={inputRef}
        style={styles.input}
        keyboardType="number-pad"
        inputMode="numeric"
        maxLength={length}
        value={value}
        onChangeText={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoFocus={autoFocus}
        editable={editable}
        textContentType="oneTimeCode"
        autoComplete="one-time-code"
        importantForAutofill="yes"
        caretHidden
        contextMenuHidden
        accessibilityRole="text"
        accessibilityLabel="Verification code"
      />
    </View>
  );
});

const CELL_SIZE = 48;
const ROW_HEIGHT = CELL_SIZE + 8;

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    height: ROW_HEIGHT,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'center',
    alignItems: 'center',
    height: ROW_HEIGHT,
  },
  cell: {
    width: CELL_SIZE,
    height: ROW_HEIGHT,
    borderRadius: radii.base,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellDisabled: { opacity: 0.5 },
  dot: { width: 12, height: 12, borderRadius: radii.full },
  input: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    color: 'transparent',
    backgroundColor: 'transparent',
    // iOS Safari auto-zooms any focused input with font-size < 16px. The text
    // is transparent and the box is sized by the wrapper, so 16px is invisible
    // here — it only exists to suppress the zoom on web. Native never zooms.
    fontSize: Platform.OS === 'web' ? 16 : 1,
    textAlign: 'center',
    // The input spans the whole row; suppress the browser's blue focus ring so
    // it doesn't draw a rectangle around all cells. Cells show their own focus
    // state (active-cell border). Same pattern as components/Input.tsx.
    outlineColor: 'transparent',
    outlineStyle: 'solid',
    outlineWidth: 0,
  },
});

const lightStyles = StyleSheet.create({
  cell: { borderColor: colors.border, backgroundColor: colors.surface },
  cellActive: { borderColor: colors.primary },
  cellError: { borderColor: colors.danger },
  dot: { backgroundColor: colors.primary },
  dotError: { backgroundColor: colors.danger },
});

const darkStyles = StyleSheet.create({
  cell: {
    borderColor: colors.pinCellOnDarkBorder,
    backgroundColor: colors.pinCellOnDarkFill,
  },
  cellActive: { borderColor: colors.electricGreen },
  cellError: { borderColor: colors.dangerBright },
  dot: { backgroundColor: colors.frost },
  dotError: { backgroundColor: colors.dangerBright },
});
